import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ConfirmModalProps {
  /** The question, as a question. */
  title: string;
  /** What else goes with it — usually what the action takes down too. */
  body?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** A destructive confirmation reads as one. */
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Everything irreversible asks first, and asks the same way. `window.confirm`
 * did that too, but it cannot say which fund, in the product's own voice, and
 * it blocks the tab while it waits.
 */
export function ConfirmModal(props: ConfirmModalProps): React.JSX.Element {
  const titleId = useId();
  const [confirmButton, setConfirmButton] = useState<HTMLButtonElement | null>(null);

  const { onCancel } = props;

  useEffect(() => {
    confirmButton?.focus();
  }, [confirmButton]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.preventDefault();
      onCancel();
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onCancel]);

  return createPortal(
    <div
      className="modal-backdrop"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 id={titleId} className="modal-title">
          {props.title}
        </h2>
        {props.body !== undefined && <p className="modal-body subtle">{props.body}</p>}
        <div className="modal-actions">
          <button className="button" type="button" onClick={onCancel}>
            {props.cancelLabel ?? 'Cancel'}
          </button>
          <button
            ref={setConfirmButton}
            className={props.danger === true ? 'button danger' : 'button primary'}
            type="button"
            onClick={props.onConfirm}
          >
            {props.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
